/**
 * Common NavigationCard component used by other modules as a reusable component which returns the ui for a product rail horizontal or a product rail vertical.
 *
 * @module views/components/NavigationCard
 * @memberof -Common
 */
import './NavigationCard.scss';

import React from 'react';

import PropTypes from 'prop-types';

import PaletteNavigationCard from '@ulta/core/adapters/palette/components/PaletteNavigationCard/PaletteNavigationCard';
import Image from '@ulta/core/components/Image/Image';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';


/**
 * Represents a NavigationCard component
 *
 * @method
 * @param {NavigationCardProps} props - React properties passed from composition
 * @returns nNavigationCard
 */
const NavigationCard = function( props ){
  const {
    action,
    altText,
    backgroundColor,
    image,
    imageUrl,
    imageMetaData,
    shopAllLabel,
    shopAllAction,
    outlineOption,
    customClassName
  } = props;

  if( !shopAllAction && !shopAllLabel && !action && !image && !imageUrl ){
    return null;
  }

  const formattedImageUrl = image ? image.imageUrl : imageUrl;
  const preferShopAll = shopAllAction && shopAllLabel && !formattedImageUrl;
  const preferredAction = preferShopAll ? shopAllAction : action;
  const formattedTextOnlyLabel = preferShopAll ? shopAllLabel : undefined;
  const formattedBorderColor = outlineOption ? 'border-xsubtle' : undefined;

  /**
   * Builds the external media element expected by the palette component
   *
   * @type {JSX.Element|undefined}
   */
  const externalMediaElement = formattedImageUrl ? (
    <Image
      alt={ altText }
      src={ formattedImageUrl }
      metaData={ {
        imageTemplate: 'ProductCardNeutralBG',
        ...( imageMetaData && imageMetaData )
      } }
    />
  ) : undefined;

  /**
   * Maps  background color token to palette background color token
   * Defaults to 'background-brand-02' if no match is found
   *
   * @type {string}
   */
  const mappedBackgroundColor = preferShopAll ? 'background-brand-02' : PALETTE_COLOR_MAPPING[ backgroundColor ];

  return (
    <Link_Huge
      target='_self'
      action={ preferredAction }
      className='NavigationCard'
    >
      <PaletteNavigationCard
        externalMediaElement={ externalMediaElement }
        backgroundColor={ mappedBackgroundColor }
        actionLabel={ preferredAction?.label }
        borderColor={ formattedBorderColor }
        textOnlyLabel={ formattedTextOnlyLabel }
        customClassName={ customClassName }
      />
    </Link_Huge>
  );
};

/**
 * Mapping of background color tokens to palette background color tokens
 *
 * @type {Object.<string, string>}
 */
const PALETTE_COLOR_MAPPING = {
  'orange-100': 'background-brand-01',
  'magenta-100': 'background-brand-02',
  'fire-100': 'background-brand-03',
  'plum-100': 'background-brand-04',
  'neutral-25': 'background-xsubtle',
  'white': 'content-knockout'
};

/**
 * Property type definitions
 * @typedef NavigationCardProps
 * @type {object}
 * @property {Object} action - Primary navigation action for the NavigationCard
 * @property {string} altText - Alternate text for the NavigationCard image
 * @property {string} backgroundColor - Background color token for the NavigationCard circle container
 * @property {string} customClassName - customClassName to adjust the container
 * @property {Object} image - Image object containing the image URL (expects image.imageUrl)
 * @property {string} imageUrl - Image URL used when image object is not provided
 * @property {Object} imageMetaData - Additional metadata for the NavigationCard image
 * @property {object} outlineOption - True if we want a border; otherwise, false
 * @property {Object} shopAllAction - Action used when rendering a text-only NavigationCard
 * @property {string} shopAllLabel - Text displayed inside the NavigationCard circle (e.g. Shop All, View All)
*/

export const propTypes =  {
  action: PropTypes.object,
  altText: PropTypes.string,
  backgroundColor: PropTypes.string,
  customClassName: PropTypes.string,
  image: PropTypes.object,
  imageUrl: PropTypes.string,
  imageMetaData: PropTypes.object,
  outlineOption: PropTypes.bool,
  shopAllAction: PropTypes.object,
  shopAllLabel: PropTypes.string
};

NavigationCard.propTypes = propTypes;

export default NavigationCard;
